import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Edit, Plus, Trash2, UserRound } from 'lucide-react';
import { initials, formatDate } from '@/shared/lib/format';
import { useConfirm } from '@/shared/components/confirm';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { ActionIconButton } from '@/shared/components/action-icon-button';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Skeleton } from '@/shared/ui/skeleton';
import type { NpsUser } from '@/shared/types/nps';
import { NPS_ROLE_LABEL, primaryNpsRole } from '../constants/roles';
import { formatCpfCnpj } from '../utils/format-cpf-cnpj';
import { deleteNpsUser, useNpsUsers } from '../api/use-nps';
import { useNpsAdminGuard } from '../hooks/use-nps-access';
import { NpsForbidden } from '../components/nps-forbidden';
import { NpsUserFormDialog } from '../components/users/nps-user-form-dialog';
import { useState } from 'react';

export function NpsUsersPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { allowed, isLoading: guardLoading } = useNpsAdminGuard();
  const { data = [], isLoading } = useNpsUsers();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<NpsUser | null>(null);

  if (!guardLoading && !allowed) return <NpsForbidden />;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['nps', 'users'] });

  const remove = async (u: NpsUser) => {
    const ok = await confirm({ title: 'Remover usuário', description: `Remover ${u.name}?`, confirmLabel: 'Remover', variant: 'destructive' });
    if (!ok) return;
    try {
      await deleteNpsUser(u.id);
      toast.success('Usuário removido.');
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao remover.');
    }
  };

  return (
    <>
      <PageHeader
        title="Usuários"
        description={`${data.length} usuário(s) cadastrado(s). Gerencie quem tem acesso à plataforma NPS.`}
        actions={
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }}>
            <Plus className="size-4" /> Novo usuário
          </Button>
        }
      />

      {isLoading || guardLoading ? (
        <div className="space-y-2.5">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
      ) : data.length === 0 ? (
        <EmptyState icon={UserRound} title="Nenhum usuário" description="Cadastre o primeiro usuário NPS." />
      ) : (
        <ul className="space-y-2.5">
          {data.map((u) => {
            const role = primaryNpsRole(u.roles);
            return (
              <li key={u.id}>
                <Card className="flex flex-col gap-3 p-3.5 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <Avatar className="size-10 shrink-0">
                      <AvatarFallback>{initials(`${u.name} ${u.last_name ?? ''}`)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{u.name} {u.last_name}</p>
                      <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {u.tax_identifier ? formatCpfCnpj(u.tax_identifier) : '—'}
                        {u.created_at ? ` · ${formatDate(u.created_at)}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
                    {role ? (
                      <Badge variant="secondary" className="font-normal">
                        {NPS_ROLE_LABEL[role]}
                      </Badge>
                    ) : null}
                    <div className="flex shrink-0 items-center gap-1.5">
                      <ActionIconButton label="Editar usuário" onClick={() => { setEditing(u); setDialogOpen(true); }}>
                        <Edit className="size-4" />
                      </ActionIconButton>
                      <ActionIconButton label="Remover usuário" onClick={() => remove(u)} className="text-destructive">
                        <Trash2 className="size-4" />
                      </ActionIconButton>
                    </div>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <NpsUserFormDialog open={dialogOpen} onOpenChange={setDialogOpen} user={editing} onSaved={refresh} />
    </>
  );
}
